import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AdminNav from '../../components/admin/AdminNav';
import api from '../../utils/api';
import { getCurrentUser } from '../../utils/auth';
import InfoModal from '../../components/common/InfoModal';
import ConfirmationModal from '../../components/common/ConfirmationModal';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY'
];

const ANIMAL_OPTIONS = ['Beef', 'Lamb', 'Sheep', 'Goat'];
const PORTION_OPTIONS = ['Full', 'Half', 'Quarter'];

export default function CreateGroup() {
  const [name, setName] = useState('');
  const [associatedSupplier, setAssociatedSupplier] = useState(null);
  const [supplierList, setSupplierList] = useState([]);
  const [animal, setAnimal] = useState('');
  const [totalCost, setTotalCost] = useState('');
  const [totalPortions, setTotalPortions] = useState('');
  const [portionsAvailable, setPortionsAvailable] = useState([]);
  const [state, setState] = useState('WA');
  const [emailList, setEmailList] = useState('tibebeasfaw@outlook.com');

  const [loadingSupplier, setLoadingSupplier] = useState(true);
  const [hasSupplier, setHasSupplier] = useState(true);

  // Modals
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [infoModalMessage, setInfoModalMessage] = useState('');
  const [infoModalTitle, setInfoModalTitle] = useState('Notice');
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const userId = currentUser?.userId;
  const isSuperAdmin = currentUser?.role === 'superadmin';
  const isSupplierAdmin = currentUser?.role === 'supplieradmin';

  const showInfo = (message, title = 'Notice') => {
    setInfoModalMessage(message);
    setInfoModalTitle(title);
    setInfoModalOpen(true);
  };

  // Load suppliers
  useEffect(() => {
    const loadSupplier = async () => {
      try {
        if (isSuperAdmin) {
          const res = await api.get('/api/suppliers');
          const list = Array.isArray(res.data) ? res.data : [];
          setSupplierList(list);
          if (list.length > 0) setAssociatedSupplier(list[0]);
          else setHasSupplier(false);
        } else {
          const res = await api.get('/api/suppliers/my-suppliers');
          const list = Array.isArray(res.data) ? res.data : [];
          if (list.length > 0) setAssociatedSupplier(list[0]);
          else {
            setAssociatedSupplier(null);
            setHasSupplier(false);
            toast.warn('Your account is not linked to a supplier.');
          }
        }
      } catch (err) {
        console.error('Error loading supplier:', err);
        setAssociatedSupplier(null);
        setHasSupplier(false);
        toast.error('Error loading your supplier info.');
      } finally {
        setLoadingSupplier(false);
      }
    };
    loadSupplier();
  }, [isSuperAdmin, isSupplierAdmin]);

  const handlePortionChange = (e) => {
    const selected = Array.from(e.target.selectedOptions).map(o => o.value);
    setPortionsAvailable(selected);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Basic validation
    if (!associatedSupplier?._id) {
      showInfo('No valid supplier linked. Cannot create group.', 'Error');
      return;
    }
    if (!name || !animal || !totalCost || !totalPortions || portionsAvailable.length === 0 || !state) {
      showInfo('All fields are required.', 'Error');
      return;
    }
    if (Number(totalCost) <= 0 || Number(totalPortions) <= 0) {
      showInfo('Cost and portions must be greater than zero.', 'Error');
      return;
    }

    // Open confirmation modal
    setConfirmModalOpen(true);
  };

  const handleConfirmCreate = async () => {
    setConfirmModalOpen(false);

    // Validate emails
    const emails = emailList
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0);
    const invalidEmails = emails.filter(email => !/^\S+@\S+\.\S+$/.test(email));
    if (invalidEmails.length > 0) {
      showInfo(`Invalid email(s): ${invalidEmails.join(', ')}`, 'Error');
      return;
    }

    // Prepare payload
    const payload = {
      name,
      animal,
      totalCost: Number(totalCost),
      totalPortions: Number(totalPortions),
      portionsAvailable,
      state,
      supplierId: associatedSupplier._id,
      createdBy: userId,
      inviteEmails: emails,
    };

    try {
      await api.post('/api/groups/create', payload);
      showInfo(`Group "${name}" created successfully!`, 'Success');
      setTimeout(() => navigate(-1), 1500);
    } catch (err) {
      console.error('Create error:', err);
      const msg = err.response?.data?.message || 'Failed to create group.';
      showInfo(msg, 'Error');
    }
  };

  const costPerFullPortion =
    Number(totalPortions) > 0 && Number(totalCost) > 0
      ? (Number(totalCost) / Number(totalPortions)).toFixed(2)
      : '0.00';

  if (loadingSupplier) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Loading supplier info...</p>
      </div>
    );
  }

  if (!hasSupplier) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-600">No supplier linked to your account. Contact admin.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminNav />
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />
      <div className="max-w-md mx-auto mt-10 p-6 bg-white rounded shadow">
        <h2 className="text-2xl font-bold mb-6 text-center">Create New Group</h2>
        <form onSubmit={handleSubmit} className="space-y-4">

          {/* Group Name */}
          <div>
            <label className="block text-sm font-medium mb-1">Group Name</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full border p-2 rounded" required />
          </div>

          {/* Supplier */}
          <div>
            <label className="block text-sm font-medium mb-1">Supplier</label>
            {isSuperAdmin ? (
              <select
                value={associatedSupplier?._id || ''}
                onChange={e => {
                  const selected = supplierList.find(s => s._id === e.target.value);
                  setAssociatedSupplier(selected || null);
                }}
                className="w-full border p-2 rounded"
                required
              >
                <option value="">Select Supplier</option>
                {supplierList.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
              </select>
            ) : (
              <input type="text" value={associatedSupplier?.name || 'Unavailable'} className="w-full border p-2 bg-gray-100 rounded" readOnly disabled />
            )}
          </div>

          {/* Animal */}
          <div>
            <label className="block text-sm font-medium mb-1">Animal</label>
            <select value={animal} onChange={e => setAnimal(e.target.value)} className="w-full border p-2 rounded" required>
              <option value="">Select Animal</option>
              {ANIMAL_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>

          {/* Total Cost */}
          <div>
            <label className="block text-sm font-medium mb-1">Total Cost ($)</label>
            <input type="number" min="1" step="0.01" value={totalCost} onChange={e => setTotalCost(e.target.value)} className="w-full border p-2 rounded" required />
          </div>

          {/* Total Portions */}
          <div>
            <label className="block text-sm font-medium mb-1">Total Portions</label>
            <input type="number" min="1" value={totalPortions} onChange={e => setTotalPortions(e.target.value)} className="w-full border p-2 rounded" required />
          </div>

          {/* Cost per portion */}
          <div className="text-gray-600">
            💲 Cost per Full Portion: <strong>${costPerFullPortion}</strong>
          </div>

          {/* Portions Available */}
          <div>
            <label className="block text-sm font-medium mb-1">Portion Options</label>
            <select multiple value={portionsAvailable} onChange={handlePortionChange} className="w-full border p-2 rounded" required>
              {PORTION_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>

          {/* State */}
          <div>
            <label className="block text-sm font-medium mb-1">State</label>
            <select value={state} onChange={e => setState(e.target.value)} className="w-full border p-2 rounded" required>
              <option value="">Select State</option>
              {US_STATES.map(st => <option key={st} value={st}>{st}</option>)}
            </select>
          </div>

          {/* Invite Emails */}
          <div>
            <label className="block text-sm font-medium mb-1">Invite Emails (comma separated)</label>
            <textarea value={emailList} onChange={e => setEmailList(e.target.value)} className="w-full border p-2 rounded" placeholder="example1@example.com, example2@example.com" rows={3} />
          </div>

          {/* Buttons */}
          <div className="flex justify-between">
            <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">Create</button>
            <button type="button" className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600" onClick={() => navigate(-1)}>Cancel</button>
          </div>
        </form>
      </div>

      {/* Modals */}
      <InfoModal isOpen={infoModalOpen} onClose={() => setInfoModalOpen(false)} title={infoModalTitle} message={infoModalMessage} />
      <ConfirmationModal
        isOpen={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleConfirmCreate}
        title="Confirm Group Creation"
        message={`Are you sure you want to create the group "${name}"?`}
        confirmLabel="Create"
        cancelLabel="Cancel"
      />
    </div>
  );
}
